import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Landing.css';
import '../../components/Header/Header.css';
import {
  FaShieldAlt,
  FaUsers,
  FaHome,
  FaBrain,
  FaChartBar,
  FaCheckCircle,
  FaStar,
  FaArrowRight,
  FaAward,
  FaChevronDown,
  FaChevronUp,
  FaUserCheck,
  FaFileAlt,
  FaHandshake,
  FaFacebook,
  FaLinkedin,
  FaInstagram,
  FaWhatsapp,
  FaTiktok,
  FaMobile,
  FaHandHoldingUsd,
  FaMapMarkerAlt,
  FaTimes
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import logo from '../../assets/images/logo.png';
import { getPublicBnbUnits, getPublicProperties, getPublicPropertyById } from '../../api/services/propertyService';
import API_BASE_URL from '../../api/config/apiConfig';
import { isExclusiveBnbPublicProperty, buildPropertyDetailsSearch } from '../../utils/publicListingScope';
import { useCookieConsent } from '../../context/CookieConsentContext';
const HERO_IMAGE_URL = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1400&q=80';
const LANDLORD_BENEFITS_IMAGE_URL = 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=900&q=80';
const AIRBNB_HOST_BENEFITS_IMAGE_URL = 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=900&q=80';
const TENANT_BENEFITS_IMAGE_URL = 'https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&w=900&q=80';
const SHORT_STAY_GUESTS_IMAGE_URL = 'https://images.unsplash.com/photo-1496417263034-38ec4f0b665a?auto=format&fit=crop&w=900&q=80';

const DOWNLOAD_PROMPT_DISMISS_KEY = 'tenantscoreDownloadPromptDismissedAt';
const DOWNLOAD_PROMPT_SNOOZE_MS = 24 * 60 * 60 * 1000;

const isAndroidMobileBrowser = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  const isAndroid = /Android/i.test(ua);
  const isMobileViewport = window.innerWidth <= 768;
  return isAndroid && isMobileViewport;
};

const isStandaloneMode = () => {
  if (typeof window === 'undefined') return false;
  const standaloneMedia = window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone = window.navigator.standalone === true;
  return standaloneMedia || iosStandalone;
};

const parseRatingValue = (row) => {
  const raw = row?.avgRating ?? row?.averageRating ?? row?.rating ?? 0;
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
};

const parseRatingCount = (row) => {
  const raw = row?.ratingCount ?? row?.totalRatings ?? row?.reviewsCount ?? 0;
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
};

const isPropertyForSale = (property) => {
  const listingType = String(property?.listingType || '').toLowerCase();
  const transactionType = String(property?.transactionType || '').toLowerCase();
  const purpose = String(property?.purpose || property?.propertyPurpose || '').toLowerCase();

  return Boolean(
    property?.isForSale ||
    property?.forSale ||
    listingType.includes('sale') ||
    listingType.includes('buy') ||
    transactionType.includes('sale') ||
    transactionType.includes('buy') ||
    purpose.includes('sale') ||
    purpose.includes('buy') ||
    Number(property?.salePrice || property?.sellingPrice || property?.askingPrice || 0) > 0
  );
};

const Landing = () => {
  const navigate = useNavigate();
  const { openCookieSettings } = useCookieConsent();
  const [realBnbListings, setRealBnbListings] = useState([]);
  const [realRentalListings, setRealRentalListings] = useState([]);
  const [realBuyListings, setRealBuyListings] = useState([]);
  const [isListingsLoading, setIsListingsLoading] = useState(true);
  const [activeListingTab, setActiveListingTab] = useState('rentals');
  const [activeNavSection, setActiveNavSection] = useState('home');
  const listingCarouselRef = useRef(null);

  useEffect(() => {
    const fetchListings = async () => {
      setIsListingsLoading(true);
      try {
        // Parallelize BnB and Rental requests to avoid waterfall
        const [bnbRes, rentalRes] = await Promise.all([
          getPublicBnbUnits(),
          getPublicProperties({ page: 0, size: 10 })
        ]);

        // Process BnB data
        const bnbRows = Array.isArray(bnbRes?.data) ? bnbRes.data : [];
        const mappedBnbs = bnbRows.slice(0, 10).map((row, idx) => {
          const photos = Array.isArray(row?.photos) ? row.photos.filter(Boolean) : [];
          let finalImgUrl = "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&q=80&w=400&h=300";

          if (photos[0]) {
            finalImgUrl = photos[0].startsWith('/') ? `${API_BASE_URL}${photos[0]}` : photos[0];
          }

          return {
            id: `${row?.propertyId || row?.propertyID || 'bnb'}-${idx}`,
            propertyId: row?.propertyId || row?.propertyID,
            unitId: row?.unitId || row?.unitID,
            type: "BnB",
            title: row?.propertyName || row?.unitName || 'BnB Listing',
            location: row?.propertyAddress || 'Location not specified',
            distance: "",
            price: `KES ${(row?.pricePerNight || 0).toLocaleString()} / night`,
            image: finalImgUrl,
            avgRating: parseRatingValue(row),
            ratingCount: parseRatingCount(row)
          };
        });
        // Enrich BnB cards with property-level ratings when unit payload omits them.
        const propertyRatingCache = new Map();
        const mappedBnbsWithRatings = await Promise.all(
          mappedBnbs.map(async (bnb) => {
            const propertyId = bnb?.propertyId;
            if (!propertyId) return bnb;
            try {
              if (!propertyRatingCache.has(propertyId)) {
                const detail = await getPublicPropertyById(propertyId, false);
                propertyRatingCache.set(propertyId, detail?.data || detail || {});
              }
              const propertyData = propertyRatingCache.get(propertyId);
              return {
                ...bnb,
                avgRating: parseRatingValue(propertyData) || bnb.avgRating,
                ratingCount: parseRatingCount(propertyData) || bnb.ratingCount,
              };
            } catch (_) {
              return bnb;
            }
          })
        );
        setRealBnbListings(mappedBnbsWithRatings);

        // Process Rental data - REMOVED the nested per-property detail loop to speed up initial load
        const payload = rentalRes?.data || rentalRes;
        const currentRentals = payload?.content || [];
        const bnbPropertyIds = new Set(
          bnbRows
            .map((row) => row?.propertyId ?? row?.propertyID)
            .filter((v) => v != null && v !== '')
            .map((v) => String(v))
        );
        const rentalScopeSummaries = currentRentals.filter(
          (prop) => !isExclusiveBnbPublicProperty(prop, bnbPropertyIds)
        );

        // Initial mapping for all rentals
        const mappedRentals = rentalScopeSummaries.map((prop, idx) => {
          const area = prop.location?.area || '';
          const city = prop.location?.city || '';
          const locStr = [area, city].filter(Boolean).join(', ') || prop.address || 'Location not specified';
          const coverImg = (Array.isArray(prop.imageUrls) && prop.imageUrls.length > 0 ? prop.imageUrls[0] : prop.imageUrl);

          let finalImgUrl = "https://images.unsplash.com/photo-1502672260266-1c1f5135ec9c?auto=format&fit=crop&q=80&w=400&h=300";
          if (coverImg) {
            finalImgUrl = coverImg.startsWith('/') ? `${API_BASE_URL}${coverImg}` : coverImg;
          }

          // Default placeholder price
          const rentValue = prop.lowestRent || prop.rent || prop.price || 0;
          const priceText = rentValue > 0
            ? `KES ${Number(rentValue).toLocaleString()} / month`
            : 'Price on request';

          return {
            id: `${prop.id || 'rent'}-${idx}`,
            propertyId: prop.id,
            type: "Rental",
            title: prop.name || 'Rental Property',
            location: locStr,
            distance: "",
            price: priceText,
            image: finalImgUrl,
            avgRating: parseRatingValue(prop),
            ratingCount: parseRatingCount(prop)
          };
        });

        const mappedBuyListings = rentalScopeSummaries
          .filter((prop) => isPropertyForSale(prop))
          .map((prop, idx) => {
            const area = prop.location?.area || '';
            const city = prop.location?.city || '';
            const locStr = [area, city].filter(Boolean).join(', ') || prop.address || 'Location not specified';
            const coverImg = (Array.isArray(prop.imageUrls) && prop.imageUrls.length > 0 ? prop.imageUrls[0] : prop.imageUrl);

            let finalImgUrl = "https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&q=80&w=400&h=300";
            if (coverImg) {
              finalImgUrl = coverImg.startsWith('/') ? `${API_BASE_URL}${coverImg}` : coverImg;
            }

            const buyPriceValue = Number(
              prop.salePrice || prop.sellingPrice || prop.askingPrice || prop.price || 0
            );

            return {
              id: `${prop.id || 'buy'}-${idx}`,
              propertyId: prop.id,
              type: "Buy",
              title: prop.name || 'Property for Sale',
              location: locStr,
              distance: "",
              price: buyPriceValue > 0
                ? `KES ${buyPriceValue.toLocaleString()}`
                : 'Price on request',
              image: finalImgUrl,
              avgRating: parseRatingValue(prop),
              ratingCount: parseRatingCount(prop)
            };
          });
        setRealBuyListings(mappedBuyListings);

        // 🚀 PRIORITY BATCHING: Fetch details for the first 4 properties (visible ones) upfront
        const priorityBatch = mappedRentals.slice(0, 4);
        const remainingBatch = mappedRentals.slice(4);

        if (priorityBatch.length > 0) {
          try {
            const priorityResults = await Promise.all(
              priorityBatch.map(async (prop) => {
                const fullProp = await getPublicPropertyById(prop.propertyId, true);
                const propertyData = fullProp?.data || fullProp;
                let rentValue = propertyData?.lowestRent || propertyData?.rent || propertyData?.price || 0;

                if ((!rentValue || rentValue === 0) && Array.isArray(propertyData?.units)) {
                  const rents = propertyData.units
                    .map(u => u.monthlyRent || u.rent)
                    .filter(r => r != null && r > 0);
                  if (rents.length > 0) rentValue = Math.min(...rents);
                }

                if (rentValue > 0) {
                  return { ...prop, price: `KES ${Number(rentValue).toLocaleString()} / month` };
                }
                return prop;
              })
            );

            // Update state with priority ones ready
            setRealRentalListings([...priorityResults, ...remainingBatch]);
          } catch (err) {
            console.error("Priority batch fetch error:", err);
            setRealRentalListings(mappedRentals);
          }
        } else {
          setRealRentalListings(mappedRentals);
        }

        // Deactivate loading state so user sees complete visible cards
        setIsListingsLoading(false);

        // 🚄 Phase 2: Fetch the remaining properties in the background
        if (remainingBatch.length > 0) {
          remainingBatch.forEach(async (prop) => {
            try {
              const fullProp = await getPublicPropertyById(prop.propertyId, true);
              const propertyData = fullProp?.data || fullProp;
              let rentValue = propertyData?.lowestRent || propertyData?.rent || propertyData?.price || 0;

              if ((!rentValue || rentValue === 0) && Array.isArray(propertyData?.units)) {
                const rents = propertyData.units
                  .map(u => u.monthlyRent || u.rent)
                  .filter(r => r != null && r > 0);
                if (rents.length > 0) rentValue = Math.min(...rents);
              }

              if (rentValue > 0) {
                const priceText = `KES ${Number(rentValue).toLocaleString()} / month`;
                setRealRentalListings(prev =>
                  prev.map(p => p.propertyId === prop.propertyId ? { ...p, price: priceText } : p)
                );
              }
            } catch (err) {
              console.error(`Background price fetch error for ${prop.propertyId}:`, err);
            }
          });
        }
      } catch (err) {
        console.error("Error fetching public listings for landing page:", err);
        setIsListingsLoading(false);
      }
    };
    fetchListings();
  }, []);
  const [openFaq, setOpenFaq] = useState(null);
  const [downloadPromptVisible, setDownloadPromptVisible] = useState(false);
  const [downloadPromptClosing, setDownloadPromptClosing] = useState(false);
  const autoHideTimerRef = useRef(null);
  const touchStartYRef = useRef(null);
  const touchStartXRef = useRef(null);
  const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.tenantscore.tenants';

  const clearAutoHideTimer = useCallback(() => {
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }
  }, []);

  const closeDownloadPrompt = useCallback(
    (rememberDismiss = true) => {
      clearAutoHideTimer();
      setDownloadPromptClosing(true);
      window.setTimeout(() => {
        setDownloadPromptVisible(false);
        setDownloadPromptClosing(false);
      }, 180);

      if (rememberDismiss) {
        localStorage.setItem(DOWNLOAD_PROMPT_DISMISS_KEY, String(Date.now()));
      }
    },
    [clearAutoHideTimer]
  );

  const scheduleDownloadPromptAutoHide = useCallback(() => {
    clearAutoHideTimer();
    autoHideTimerRef.current = window.setTimeout(() => {
      closeDownloadPrompt(false);
    }, 10000);
  }, [clearAutoHideTimer, closeDownloadPrompt]);

  useEffect(() => {
    if (!isAndroidMobileBrowser() || isStandaloneMode()) return undefined;

    const lastDismissedAt = Number(localStorage.getItem(DOWNLOAD_PROMPT_DISMISS_KEY) || 0);
    if (Date.now() - lastDismissedAt < DOWNLOAD_PROMPT_SNOOZE_MS) return undefined;

    setDownloadPromptVisible(true);
    scheduleDownloadPromptAutoHide();

    return () => {
      clearAutoHideTimer();
    };
  }, [clearAutoHideTimer, scheduleDownloadPromptAutoHide]);

  const handleDownloadClick = () => {
    clearAutoHideTimer();
    window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
    closeDownloadPrompt(true);
  };

  const handlePromptTouchStart = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    touchStartYRef.current = touch.clientY;
    touchStartXRef.current = touch.clientX;
  };

  const handlePromptTouchEnd = (event) => {
    const touch = event.changedTouches?.[0];
    if (!touch) return;

    const startY = touchStartYRef.current;
    const startX = touchStartXRef.current;
    touchStartYRef.current = null;
    touchStartXRef.current = null;

    if (typeof startY !== 'number' || typeof startX !== 'number') return;

    const deltaY = startY - touch.clientY;
    const deltaX = Math.abs(startX - touch.clientX);
    if (deltaY > 40 && deltaX < 100) {
      closeDownloadPrompt(true);
    }
  };

  const handleStartFree = () => {
    navigate('/signup');
  };

  const handleLearnMore = () => {
    document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    if (sectionId === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const orderedSections = [
      'home',
      'explore-listings',
      'landlords-benefits',
      'how-it-works',
      'about',
      'contact'
    ];

    const updateActiveSection = () => {
      const scrollPosition = window.scrollY + 130;
      let currentSection = 'home';

      orderedSections.forEach((sectionId) => {
        if (sectionId === 'home') return;
        const element = document.getElementById(sectionId);
        if (!element) return;
        if (scrollPosition >= element.offsetTop) {
          currentSection = sectionId;
        }
      });

      if (window.scrollY < 50) {
        currentSection = 'home';
      }
      setActiveNavSection(currentSection);
    };

    updateActiveSection();
    window.addEventListener('scroll', updateActiveSection, { passive: true });
    return () => window.removeEventListener('scroll', updateActiveSection);
  }, []);

  const handleCreateProfile = () => {
    navigate('/signup');
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const activeListings = activeListingTab === 'bnb'
    ? realBnbListings
    : activeListingTab === 'buy'
      ? realBuyListings
      : realRentalListings;
  const featuredListings = activeListings.slice(0, 10);

  const handleListingCardClick = (item) => {
    const propertyId = item.propertyId || item.id;
    const search = buildPropertyDetailsSearch({
      unitId: item.unitId,
      scope: activeListingTab === 'bnb' ? 'BNB' : activeListingTab === 'rentals' ? 'RENTAL' : undefined,
    });
    navigate(`/property/${propertyId}${search ? `?${search}` : ''}`);
  };

  const handleListingTabChange = (tab) => {
    setActiveListingTab(tab);
    if (listingCarouselRef.current) {
      listingCarouselRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  };

  const scrollListingCarousel = (direction) => {
    if (!listingCarouselRef.current) return;
    const scrollAmount = listingCarouselRef.current.clientWidth * 0.9;
    listingCarouselRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  const faqs = [
    {
      question: "How does TenantScore help landlords?",
      answer: "TenantScore provides comprehensive tenant screening, rent tracking, and property management tools. You can verify tenant history, track payments, manage multiple properties, and reduce late payments by up to 80%."
    },
    {
      question: "Is TenantScore suitable for Airbnb hosts?",
      answer: "Absolutely! TenantScore helps Airbnb hosts screen guests, manage bookings, track payments, and build a reputation system. You can see guest history and make informed decisions about who to host."
    },
    {
      question: "How does the behavior scoring work?",
      answer: "Our behavior scoring system tracks payment history, property care, communication, and adherence to lease terms. Good behavior leads to higher scores, which can result in faster approvals and lower deposits."
    },
    {
      question: "What information do I need to sign up?",
      answer: "Signing up is quick and easy. You'll need basic information like your name, email, phone number, and whether you're a landlord, Airbnb host, tenant, or agent. No credit card required to start."
    },
    {
      question: "Is my data secure?",
      answer: "Yes, we take data security seriously. All information is encrypted and stored securely. We comply with data protection regulations and never share your information with third parties without consent."
    },
    {
      question: "Can I manage multiple properties?",
      answer: "Yes! Our platform is designed to handle multiple properties. Whether you're a landlord with several units or an agent managing multiple clients, TenantScore scales with your needs."
    }
  ];

  return (
    <div className="landing-page">
      {downloadPromptVisible && (
        <div
          className={`install-pwa-banner ${downloadPromptClosing ? 'is-closing' : ''}`}
          onTouchStart={handlePromptTouchStart}
          onTouchEnd={handlePromptTouchEnd}
          role="region"
          aria-label="Download app banner"
        >
          <div className="install-pwa-banner-icon" aria-hidden="true">
            <img src={logo} alt="" className="install-pwa-banner-icon-image" />
          </div>
          <div className="install-pwa-banner-copy">
            <div className="install-pwa-banner-title">TenantScore</div>
            <div className="install-pwa-banner-subtitle">Get it on Google Play</div>
          </div>
          <button type="button" className="install-pwa-banner-action" onClick={handleDownloadClick}>
            Get
          </button>
          <button
            type="button"
            className="install-pwa-banner-close"
            onClick={() => closeDownloadPrompt(true)}
            aria-label="Dismiss download banner"
          >
            <FaTimes />
          </button>
        </div>
      )}
      {/* Navigation Bar */}
      <header className="header">
        <div className="header-left">
          <div
            className="logo-container"
            role="button"
            tabIndex={0}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            onKeyPress={(e) => { if (e.key === 'Enter') window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          >
            <img src={logo} alt="TenantScore Logo" className="logo-image" />
          </div>
        </div>
        <nav className="landing-top-nav" aria-label="Primary navigation">
          <button
            className={`landing-top-nav-link ${activeNavSection === 'home' ? 'active' : ''}`}
            onClick={() => scrollToSection('home')}
          >
            Home
          </button>
          <button
            className={`landing-top-nav-link ${activeNavSection === 'explore-listings' ? 'active' : ''}`}
            onClick={() => scrollToSection('explore-listings')}
          >
            Listings
          </button>
          <button
            className={`landing-top-nav-link ${activeNavSection === 'landlords-benefits' ? 'active' : ''}`}
            onClick={() => scrollToSection('landlords-benefits')}
          >
            For Landlords
          </button>
          <button
            className={`landing-top-nav-link ${activeNavSection === 'how-it-works' ? 'active' : ''}`}
            onClick={() => scrollToSection('how-it-works')}
          >
            How It Works
          </button>
          <button
            className={`landing-top-nav-link ${activeNavSection === 'about' ? 'active' : ''}`}
            onClick={() => scrollToSection('about')}
          >
            About
          </button>
          <button
            className={`landing-top-nav-link ${activeNavSection === 'contact' ? 'active' : ''}`}
            onClick={() => scrollToSection('contact')}
          >
            Contact
          </button>
        </nav>
        <div className="header-right">
          <div className="nav-actions">
            <button className="nav-btn nav-btn-secondary" onClick={() => navigate('/login')}>
              Login
            </button>
            <button className="nav-btn nav-btn-primary" onClick={handleStartFree}>
              Create Account
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="hero-section"
        style={{
          backgroundImage: `radial-gradient(120% 85% at 0% 100%, rgba(245, 249, 255, 1) 0%, rgba(245, 249, 255, 1) 34%, rgba(245, 249, 255, 0.92) 52%, rgba(245, 249, 255, 0) 74%), linear-gradient(90deg, rgba(245, 249, 255, 1) 0%, rgba(245, 249, 255, 1) 40%, rgba(245, 249, 255, 0.92) 52%, rgba(245, 249, 255, 0.45) 64%, rgba(245, 249, 255, 0) 78%), url(${HERO_IMAGE_URL})`
        }}
      >
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              Rent Smarter, <span className="title-highlight">Host Better</span>
            </h1>
            <p className="hero-subtitle">
              Your all-in-one platform for renting, staying, and living smarter. Find homes, book stays, build your tenant score, and unlock faster approvals all in one place. Order services like cabs, food, and more right from your dashboard. Behind the scenes, landlords and short-stay BnB owners use TenantScore to screen reliably, track rent, and manage listings so you get a smoother, more trusted experience every time.
            </p>
          </div>
        </div>
      </section>

      <section id="explore-listings" className="landing-top-listings-section">
        <div className="section-container">
          <div className="landing-top-listings-tabs">
            <button
              className={`listing-tab-btn ${activeListingTab === 'rentals' ? 'active' : ''}`}
              onClick={() => handleListingTabChange('rentals')}
            >
              Rentals
            </button>
            <button
              className={`listing-tab-btn ${activeListingTab === 'bnb' ? 'active' : ''}`}
              onClick={() => handleListingTabChange('bnb')}
            >
              Short BnB Stays
            </button>
            <button
              className={`listing-tab-btn ${activeListingTab === 'buy' ? 'active' : ''}`}
              onClick={() => handleListingTabChange('buy')}
            >
              Buy Property
            </button>
          </div>

          <div className="landing-top-listings-carousel-wrap">
            {!isListingsLoading && featuredListings.length > 4 && (
              <button
                type="button"
                className="listing-carousel-nav left"
                onClick={() => scrollListingCarousel('left')}
                aria-label="Scroll listings left"
              >
                ‹
              </button>
            )}

            <div
              className="landing-top-listings-grid carousel-mode"
              ref={listingCarouselRef}
            >
              {isListingsLoading
                ? Array.from({ length: 4 }).map((_, idx) => (
                <div className="landing-top-listing-card skeleton" key={`listing-skeleton-${idx}`}>
                  <div className="landing-top-listing-image skeleton-shimmer" />
                  <div className="landing-top-listing-content">
                    <div className="landing-top-listing-line skeleton-shimmer" />
                    <div className="landing-top-listing-line short skeleton-shimmer" />
                  </div>
                </div>
              ))
                : featuredListings.map((item) => (
                <article
                  key={item.id}
                  className="landing-top-listing-card"
                  onClick={() => handleListingCardClick(item)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleListingCardClick(item);
                    }
                  }}
                >
                  <div className="landing-top-listing-image-wrap">
                    <img src={item.image} alt={item.title} className="landing-top-listing-image" loading="lazy" />
                  </div>
                  <div className="landing-top-listing-content">
                    <h3 className="landing-top-listing-title">{item.title}</h3>
                    <p className="landing-top-listing-location">
                      <FaMapMarkerAlt /> {item.location}
                    </p>
                    <div className="landing-top-listing-rating-line">
                      <span className="landing-top-listing-stars" aria-hidden="true">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <FaStar
                            key={star}
                            className={`tiny-star ${star <= Math.round(Number(item.avgRating || 0)) ? 'active' : 'empty'}`}
                          />
                        ))}
                      </span>
                      <span className="landing-top-listing-rating-value">{Number(item.avgRating || 0).toFixed(1)}</span>
                    </div>
                    <div className="landing-top-listing-meta">
                      <span className="landing-top-listing-price">{item.price}</span>
                    </div>
                  </div>
                </article>
                ))}
            </div>

            {!isListingsLoading && featuredListings.length > 4 && (
              <button
                type="button"
                className="listing-carousel-nav right"
                onClick={() => scrollListingCarousel('right')}
                aria-label="Scroll listings right"
              >
                ›
              </button>
            )}
          </div>

          {!isListingsLoading && featuredListings.length > 0 && (
            <div className="landing-top-listings-cta">
              <button
                type="button"
                className="view-all-listings-btn"
                onClick={() => navigate(`/listings?type=${activeListingTab}`, { state: { asGuest: true } })}
              >
                View All Listings <FaArrowRight />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Problems Section */}
      <section id="landlords-benefits" className="problems-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Platform Benefits</h2>
          </div>
          <div className="problems-grid">
            <div className="problem-card">
              <div className="problem-image-wrapper">
                <img src={LANDLORD_BENEFITS_IMAGE_URL} alt="Landlord Challenges" className="problem-image" />
              </div>
              <h3 className="problem-title">Benefits for Landlords</h3>
              <ul className="problem-list">
                <li>
                  <FaCheckCircle className="check-icon" />
                  <div>
                    <strong>Verified Tenants (KYC)</strong>
                    <br />
                    <span style={{ fontSize: '0.9em', color: '#6b7280' }}>Know who you’re renting to. No fakes.</span>
                  </div>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <div>
                    <strong>Tenant History & Score</strong>
                    <br />
                    <span style={{ fontSize: '0.9em', color: '#6b7280' }}>Real behavior. Real data. No guesswork.</span>
                  </div>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <div>
                    <strong>Lower Risk of Default</strong>
                    <br />
                    <span style={{ fontSize: '0.9em', color: '#6b7280' }}>Choose tenants who pay on time.</span>
                  </div>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <div>
                    <strong>Faster Screening</strong>
                    <br />
                    <span style={{ fontSize: '0.9em', color: '#6b7280' }}>Everything in one simple score.</span>
                  </div>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <div>
                    <strong>Reduced Damage</strong>
                    <br />
                    <span style={{ fontSize: '0.9em', color: '#6b7280' }}>Pick tenants with proven track records.</span>
                  </div>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <div>
                    <strong>Tenant Accountability</strong>
                    <br />
                    <span style={{ fontSize: '0.9em', color: '#6b7280' }}>People behave better when rated.</span>
                  </div>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <div>
                    <strong>Save Time & Effort</strong>
                    <br />
                    <span style={{ fontSize: '0.9em', color: '#6b7280' }}>Less chasing. More control.</span>
                  </div>
                </li>
              </ul>
            </div>
            <div className="problem-card">
              <div className="problem-image-wrapper">
                <img src={AIRBNB_HOST_BENEFITS_IMAGE_URL} alt="Airbnb Host Challenges" className="problem-image" />
              </div>
              <h3 className="problem-title">Benefits for Short Stay BnB Owners</h3>
              <ul className="problem-list">
                <li>
                  <FaCheckCircle className="check-icon" />
                  <div>
                    <strong>Verified Guests Only</strong>
                    <br />
                    <span style={{ fontSize: '0.9em', color: '#6b7280' }}>Real guests. No anonymous bookings.</span>
                  </div>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <div>
                    <strong>Guest History & Ratings</strong>
                    <br />
                    <span style={{ fontSize: '0.9em', color: '#6b7280' }}>See past behavior before accepting.</span>
                  </div>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <div>
                    <strong>Fewer Scams & Cancellations</strong>
                    <br />
                    <span style={{ fontSize: '0.9em', color: '#6b7280' }}>Only trusted bookings get through.</span>
                  </div>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <div>
                    <strong>Protect Your Property</strong>
                    <br />
                    <span style={{ fontSize: '0.9em', color: '#6b7280' }}>Avoid guests with bad history.</span>
                  </div>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <div>
                    <strong>Confident Hosting</strong>
                    <br />
                    <span style={{ fontSize: '0.9em', color: '#6b7280' }}>Accept bookings without doubt.</span>
                  </div>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <div>
                    <strong>Better Quality Guests</strong>
                    <br />
                    <span style={{ fontSize: '0.9em', color: '#6b7280' }}>Attract respectful, reliable travelers.</span>
                  </div>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <div>
                    <strong>Strong Trust System</strong>
                    <br />
                    <span style={{ fontSize: '0.9em', color: '#6b7280' }}>Better guests. Better stays.</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="problems-grid">
            <div className="problem-card">
              <div className="problem-image-wrapper">
                <img src={TENANT_BENEFITS_IMAGE_URL} alt="Tenant Benefits" className="problem-image" />
              </div>
              <h3 className="problem-title">For Tenants</h3>
              <ul className="problem-list">
                <li>
                  <FaCheckCircle className="check-icon" />
                  <span>Build a trusted rental profile</span>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <span>Get approved for houses faster</span>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <span>Access better homes & landlords</span>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <span>Unlock rent support/loans (coming soon)</span>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <span>No more endless references or guesswork</span>
                </li>
              </ul>
              <p className="tenant-highlight-description" style={{ marginTop: '1rem', fontStyle: 'italic' }}>
                "Your rent history becomes your advantage."
              </p>
            </div>

            <div className="problem-card">
              <div className="problem-image-wrapper">
                <img src={SHORT_STAY_GUESTS_IMAGE_URL} alt="Short Stay Guests Benefits" className="problem-image" />
              </div>
              <h3 className="problem-title">Short Stay Guests</h3>
              <ul className="problem-list">
                <li>
                  <FaCheckCircle className="check-icon" />
                  <span>Book with confidence. Stay without stress.</span>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <span>Wide choice of homes for every budget & style</span>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <span>Clear pricing - no hidden surprises</span>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <span>Easy, secure booking in minutes</span>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <span>Real reviews from real stays</span>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <span>Flexible options for changes & cancellations</span>
                </li>
                <li>
                  <FaCheckCircle className="check-icon" />
                  <span>Direct communication with hosts</span>
                </li>
              </ul>
              <p className="tenant-highlight-description" style={{ marginTop: '1rem', fontStyle: 'italic' }}>
                "Everything you need for a smooth stay-before you even arrive."
              </p>
            </div>
          </div>
          
          <div className="tenant-benefit-highlight">
            <div className="tenant-highlight-card">
              <div className="tenant-highlight-icon">
                <FaHandHoldingUsd />
              </div>
              <div className="tenant-highlight-content">
                <div className="tenant-badge">NEW FOR TENANTS</div>
                <h3 className="tenant-highlight-title">Access to Rent Loans</h3>
                <p className="tenant-highlight-description">
                  With a good Tenant Score, you can qualify for rent support or short-term loans when needed.
                </p>
              </div>
              <div className="tenant-highlight-action">
                <button className="btn-tenant-action" onClick={() => navigate('/login')}>
                  Build My Score <FaArrowRight style={{ marginLeft: '8px' }} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">How It Works</h2>
          </div>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-icon">
                <FaUserCheck />
              </div>
              <h3 className="step-title">Create Your Profile</h3>
              <p className="step-description">Sign up in seconds. Choose your role - landlord, Airbnb host, tenant, or agent. No credit card required.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-icon">
                <FaFileAlt />
              </div>
              <h3 className="step-title">Add Your Properties</h3>
              <p className="step-description">List your properties or units. Set up rent tracking, manage listings, and organize everything in one place.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-icon">
                <FaShieldAlt />
              </div>
              <h3 className="step-title">Screen & Manage</h3>
              <p className="step-description">Screen tenants and guests instantly. Track payments, monitor behavior, and make data-driven decisions.</p>
            </div>
            <div className="step-card">
              <div className="step-number">4</div>
              <div className="step-icon">
                <FaAward />
              </div>
              <h3 className="step-title">Build Trust & Grow</h3>
              <p className="step-description">Watch your TenantScore grow with good behavior. Get faster approvals and better rental opportunities.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="about" className="features-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Everything You Need — In One Dashboard</h2>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <FaShieldAlt />
              </div>
              <h3 className="feature-title">Smart Screening</h3>
              <p className="feature-description">Comprehensive tenant and guest screening with instant background checks, rental history verification, and behavior scoring.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <FaUsers />
              </div>
              <h3 className="feature-title">Rent & Booking Tracking</h3>
              <p className="feature-description">Automated payment tracking, booking management, and financial reports. Never miss a payment or booking again.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <FaHome />
              </div>
              <h3 className="feature-title">Property Management</h3>
              <p className="feature-description">Manage multiple properties and listings from one dashboard. Fill vacancies faster with our streamlined process.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <FaBrain />
              </div>
              <h3 className="feature-title">Behavior Insights</h3>
              <p className="feature-description">AI-powered behavior scoring rewards responsible tenants and guests with faster approvals and lower deposits.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <FaChartBar />
              </div>
              <h3 className="feature-title">Analytics & Reports</h3>
              <p className="feature-description">Detailed analytics and customizable reports help you make informed decisions and optimize your rental business.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <FaHandshake />
              </div>
              <h3 className="feature-title">Trust Network</h3>
              <p className="feature-description">Join a network of verified landlords, hosts, and tenants. Build your reputation and find trusted partners.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Build Your Rental Reputation</h2>
            <p className="benefits-intro">Our innovative behavior scoring system creates a win-win situation for landlords, hosts, and tenants.</p>
          </div>
          <div className="benefits-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <FaCheckCircle />
              </div>
              <h3 className="feature-title">Fast Approvals</h3>
              <p className="feature-description">Responsible tenants and guests get approved 3x faster with higher TenantScores</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <FaHandHoldingUsd />
              </div>
              <h3 className="feature-title">Lower Deposits</h3>
              <p className="feature-description">Good behavior history can reduce security deposits by up to 50%</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <FaFileAlt />
              </div>
              <h3 className="feature-title">Complete History</h3>
              <p className="feature-description">Track your entire rental history in one place, accessible anytime</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <FaAward />
              </div>
              <h3 className="feature-title">Better Opportunities</h3>
              <p className="feature-description">Higher scores unlock premium properties and exclusive listings</p>
            </div>
          </div>
          <div className="benefits-cta">
            <button className="btn-benefit" onClick={handleCreateProfile}>
              Create My Profile & Start Building My Score
              <FaArrowRight className="btn-icon" />
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">What Our Users Say</h2>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-stars">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className="star-icon" />
                ))}
              </div>
              <p className="testimonial-text">"TenantScore has completely transformed how I manage my properties. I've reduced late payments by 90% and saved hours every week on paperwork."</p>
              <div className="testimonial-author">
                <div className="author-avatar">JM</div>
                <div className="author-info">
                  <div className="author-name">James Mwangi</div>
                  <div className="author-role">Landlord, Nairobi</div>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-stars">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className="star-icon" />
                ))}
              </div>
              <p className="testimonial-text">"As an Airbnb host, screening guests was always a gamble. Now I can see their history and make informed decisions. My bookings are up 40%!"</p>
              <div className="testimonial-author">
                <div className="author-avatar">SK</div>
                <div className="author-info">
                  <div className="author-name">Sarah Kamau</div>
                  <div className="author-role">Airbnb Host, Mombasa</div>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-stars">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className="star-icon" />
                ))}
              </div>
              <p className="testimonial-text">"My TenantScore helped me get approved for my dream apartment in just 2 days! The landlord loved seeing my complete rental history."</p>
              <div className="testimonial-author">
                <div className="author-avatar">DO</div>
                <div className="author-info">
                  <div className="author-name">David Ochieng</div>
                  <div className="author-role">Tenant, Kisumu</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section - temporarily hidden, restore when ready
      <section className="pricing-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Choose Your Plan</h2>
          </div>
          <div className="pricing-grid">
            <div className="pricing-card">
              <h3 className="pricing-title">Landlord Plan</h3>
              <p className="pricing-purpose">Perfect for long-term rentals</p>
              <div className="pricing-price">
                <span className="price-amount price-amount-large">From KES 1,000</span>
                <span className="price-period">/month</span>
              </div>
              <ul className="pricing-features">
                <li><FaCheckCircle className="feature-check" /> Unlimited tenant screening</li>
                <li><FaCheckCircle className="feature-check" /> Rent tracking & reminders</li>
                <li><FaCheckCircle className="feature-check" /> Up to 10 properties</li>
                <li><FaCheckCircle className="feature-check" /> Analytics & reports</li>
                <li><FaCheckCircle className="feature-check" /> 24/7 support</li>
              </ul>
              <button className="btn-pricing" onClick={handleStartFree}>
                Get Started
              </button>
            </div>
            <div className="pricing-card">
              <h3 className="pricing-title">Airbnb Host Plan</h3>
              <p className="pricing-purpose">Ideal for short-term rentals</p>
              <div className="pricing-price">
                <span className="price-amount price-amount-large">From KES 1,000</span>
                <span className="price-period">/month</span>
              </div>
              <ul className="pricing-features">
                <li><FaCheckCircle className="feature-check" /> Guest screening & verification</li>
                <li><FaCheckCircle className="feature-check" /> Booking management</li>
                <li><FaCheckCircle className="feature-check" /> Up to 5 properties</li>
                <li><FaCheckCircle className="feature-check" /> Payment tracking</li>
                <li><FaCheckCircle className="feature-check" /> Priority support</li>
              </ul>
              <button className="btn-pricing" onClick={handleStartFree}>
                Get Started
              </button>
            </div>
            <div className="pricing-card">
              <h3 className="pricing-title">Agent Plan</h3>
              <p className="pricing-purpose">For property management companies</p>
              <div className="pricing-price">
                <span className="price-amount">From KES 1,000</span>
                <span className="price-period">/month</span>
              </div>
              <ul className="pricing-features">
                <li><FaCheckCircle className="feature-check" /> Up to 15 properties</li>
                <li><FaCheckCircle className="feature-check" /> Multi-user access</li>
                <li><FaCheckCircle className="feature-check" /> Advanced analytics</li>
                <li><FaCheckCircle className="feature-check" /> API access</li>
                <li><FaCheckCircle className="feature-check" /> Dedicated account manager</li>
              </ul>
              <button className="btn-pricing" onClick={() => navigate('/login')}>
                Get Started
              </button>
            </div>
          </div>
        </div>
      </section>
      */}

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Frequently Asked Questions</h2>
          </div>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button className="faq-question" onClick={() => toggleFaq(index)}>
                  <span>{faq.question}</span>
                  {openFaq === index ? (
                    <FaChevronUp className="faq-icon" />
                  ) : (
                    <FaChevronDown className="faq-icon" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div id="contact" />
        <div className="footer-container">
          <div className="footer-top">
            <div className="footer-brand">
              <h3 className="footer-brand-title">TenantScore</h3>
              <p className="footer-text">Making renting and hosting easier for everyone across Kenya.</p>
              <p className="footer-text">Contact: +254113886814</p>
              <p className="footer-text">
                Email:{' '}
                <a href="mailto:support@tenantscore.co.ke" className="footer-text-link">
                  support@tenantscore.co.ke
                </a>
              </p>
              <div className="footer-social">
                <a href="https://www.facebook.com/share/1CUS6banwc/" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Facebook">
                  <FaFacebook />
                </a>
                <a href="https://www.instagram.com/tenantscorekenya?igsh=MTZ0OXVwOGJ1OWhwZg==" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Instagram">
                  <FaInstagram />
                </a>
                <a href="https://x.com/TenantscoreKE" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Twitter">
                  <FaXTwitter />
                </a>
                <a href="https://ke.linkedin.com/in/tenantscore-kenya-607a633aa" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="LinkedIn">
                  <FaLinkedin />
                </a>
                <a href="https://wa.me" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="WhatsApp">
                  <FaWhatsapp />
                </a>
                <a href="https://www.tiktok.com/@tenantscore.kenya?_r=1&_t=ZS-93fKQMsby4e" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="TikTok">
                  <FaTiktok />
                </a>
              </div>
            </div>
            <div className="footer-links-grid">
              <div className="footer-column">
                <h4 className="footer-column-title">Product</h4>
                <button className="footer-link" onClick={handleLearnMore}>Features</button>
                <button className="footer-link" onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}>Pricing</button>
                <button className="footer-link" onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}>How It Works</button>
                <a
                  href={PLAY_STORE_URL}
                  className="footer-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaMobile style={{ marginRight: '8px', fontSize: '0.9rem' }} />
                  Download App
                </a>
              </div>
              <div className="footer-column">
                <h4 className="footer-column-title">Company</h4>
                <button className="footer-link">About Us</button>
                <Link to="/blog" className="footer-link footer-link-anchor">Blog</Link>
                <button className="footer-link">Contact</button>
              </div>
              <div className="footer-column">
                <h4 className="footer-column-title">Legal</h4>
                <Link to="/privacy" className="footer-link footer-link-anchor">Privacy Notice</Link>
                <Link to="/terms" className="footer-link footer-link-anchor">Terms of Service</Link>
                <Link to="/cookies" className="footer-link footer-link-anchor">Cookies Policy</Link>
                <button type="button" className="footer-link" onClick={openCookieSettings}>
                  Cookie settings
                </button>
                <button
                  type="button"
                  className="footer-link"
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Contact
                </button>
              </div>
              <div className="footer-column">
                <h4 className="footer-column-title">Account</h4>
                <button className="footer-link" onClick={() => navigate('/login')}>Login</button>
                <button className="footer-link" onClick={handleStartFree}>Sign Up</button>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 TenantScore. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
